/** Attribution tab — admin growth dashboard (LH-114).
 *
 * Renders first-touch attribution grouped by source / medium / campaign /
 * creator code, joined to entitlement state on the canonical users
 * document: signups → active trials → paid conversions.
 */

import { AlertCircle, Link2, RefreshCw, Tag, UserPlus, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { DateRangePicker } from "@/shared/ui/date-range-picker";
import { T } from "@/shared/ui/DesignLib";
import { Skeleton } from "@/shared/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/shared/ui/table";

import { useAttribution } from "../hooks/useAttribution";
import {
    dateRangeToMetricsRange,
    isCustomDateRange,
    metricsRangeToDateRange,
    type MetricsRange,
    type TimeRange,
} from "../lib/metricsTypes";
import MetricCard from "./MetricCard";

const RANGE_LABELS: Record<TimeRange, string> = {
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
};

function rangeLabel(range: MetricsRange): string {
  if (isCustomDateRange(range)) return `${range.from} – ${range.to}`;
  return RANGE_LABELS[range];
}

function formatPercent(part: number, total: number): string {
  if (!total) return "—";
  return `${Math.round((part / total) * 100)}%`;
}

function SectionCard({
  title,
  subtitle,
  icon: Icon,
  children,
}: {
  title: string;
  subtitle?: string;
  icon: LucideIcon;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Icon className="size-4 shrink-0 text-primary" aria-hidden="true" />
          <CardTitle className="text-sm font-semibold text-foreground">
            {title}
          </CardTitle>
        </div>
        {subtitle && (
          <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            {subtitle}
          </p>
        )}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex h-[180px] items-center justify-center rounded-[var(--radius-sm)] border border-dashed border-border">
      <p className="max-w-sm text-center text-[12px] text-muted-foreground">
        {message}
      </p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} height={104} borderRadius={8} data-testid="attribution-skeleton" />
        ))}
      </div>
      <Skeleton height={320} borderRadius={8} data-testid="attribution-skeleton" />
    </div>
  );
}

export interface AttributionTabProps {
  className?: string;
}

export default function AttributionTab({ className }: AttributionTabProps) {
  const { attribution, loading, error, range, setRange, refetch } = useAttribution("30d");
  const rows = attribution?.rows ?? [];

  return (
    <div className={className}>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1
            className="m-0 text-[1.5rem] font-bold leading-[1.1] text-foreground md:text-[2rem]"
            style={{ fontFamily: T.serif }}
          >
            Attribution
          </h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            {rangeLabel(range)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker
            value={metricsRangeToDateRange(range)}
            onChange={(dr) => {
              const next = dateRangeToMetricsRange(dr);
              setRange(next ?? "30d");
            }}
            align="end"
            maxDate={new Date()}
          />
          <Button
            variant="outline"
            size="icon-sm"
            onClick={refetch}
            disabled={loading}
            aria-label="Refresh attribution"
          >
            <RefreshCw className={loading ? "animate-spin" : undefined} />
          </Button>
        </div>
      </div>

      {error && (
        <Card className="mb-5 border-destructive/50 bg-destructive/10">
          <CardContent className="flex items-start gap-2 p-4">
            <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
            <div>
              <p className="text-[13px] font-medium text-destructive">
                Failed to load attribution
              </p>
              <p className="mt-0.5 text-[12px] text-muted-foreground">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && <LoadingSkeleton />}

      {!loading && !error && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              label="Attributed Signups"
              value={attribution?.total_attributed_signups ?? 0}
              hint="Signed up with UTM / creator tags"
              icon={UserPlus}
            />
            <MetricCard
              label="Active Trials"
              value={rows.reduce((n, r) => n + r.active_trials, 0)}
              hint="Currently on a trial plan"
              icon={Tag}
            />
            <MetricCard
              label="Paid Conversions"
              value={attribution?.total_paid ?? 0}
              hint="Ever converted to a paid plan"
              icon={Link2}
            />
            <MetricCard
              label="Signup → Paid"
              value={formatPercent(
                attribution?.total_paid ?? 0,
                attribution?.total_attributed_signups ?? 0,
              )}
              hint="Conversion across attributed signups"
              icon={RefreshCw}
            />
          </div>

          <SectionCard
            title="First-Touch Attribution"
            subtitle="Signups and paid conversions by source / campaign / creator"
            icon={Tag}
          >
            {rows.length === 0 ? (
              <EmptyState message="No attributed signups in this period. Signups arriving via UTM-tagged links, creator codes (?ref=), or short links (/r/{code}) appear here." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableHead>Medium</TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Creator Code</TableHead>
                    <TableHead className="text-right">Signups</TableHead>
                    <TableHead className="text-right">Trials</TableHead>
                    <TableHead className="text-right">Paid</TableHead>
                    <TableHead className="text-right">Conv.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, i) => (
                    <TableRow key={`${row.source}-${row.medium ?? ""}-${row.campaign ?? ""}-${row.ref ?? ""}-${i}`}>
                      <TableCell className="font-mono text-[12px]">{row.source}</TableCell>
                      <TableCell className="text-muted-foreground">{row.medium ?? "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{row.campaign ?? "—"}</TableCell>
                      <TableCell className="font-mono text-[12px]">{row.ref ?? "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.signups}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {row.active_trials}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{row.paid}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {formatPercent(row.paid, row.signups)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </SectionCard>
        </div>
      )}
    </div>
  );
}
